/**
 * Ontology 탭의 최상위 에디터 — Hero + View(Diagram|Catalog) + 사이드 MetaEditPanel + 모달 오케스트레이션.
 *
 * 설계 원칙:
 * - 편집은 모두 `useOntologyDraft` 에 모여 있고, 서버 호출은 이 컴포넌트에서만 일어난다.
 * - Save 플로: previewOntologySlice → PreviewImpactModal → 확인 → updateOntologySlice → commit + onSaved()
 * - Pack/Import/Export 는 draft 를 덮어쓰고, Save 시점까지는 서버에 영향 없음.
 * - 사이드 패널(MetaEditPanel) 은 diagram 과 catalog 양쪽에서 공유.
 */
import { useCallback, useEffect, useMemo, useState } from "react"
import type {
	AdminClient,
	DomainDef,
	KindDef,
	OntologyBlock,
	OntologyPack,
	OntologyPreset,
	OntologySlice,
	OntologyUsage,
	OntologyViolations,
	RelationDef,
	ShapeDef
} from "../../api/admin-client.ts"
import { AddEntryModal } from "./AddEntryModal.tsx"
import { ImportJsonModal } from "./ImportJsonModal.tsx"
import { loadMasterComposerData, MasterComposer } from "./MasterComposer.tsx"
import { MetaEditPanel, type MetaSelection } from "./MetaEditPanel.tsx"
import { mergePacks } from "./merge-packs.ts"
import { OntologyCatalog } from "./OntologyCatalog.tsx"
import { OntologyDiagram } from "./OntologyDiagram.tsx"
import { OntologyHero } from "./OntologyHero.tsx"
import { PackPicker } from "./PackPicker.tsx"
import { PreviewImpactModal } from "./PreviewImpactModal.tsx"
import { useOntologyDraft } from "./useOntologyDraft.ts"

type ViewKey = "diagram" | "catalog"
type AddTarget = "kind" | "domain" | "relation" | null

interface OntologyEditorProps {
	readonly slug: string
	readonly initial: OntologySlice
	readonly client: AdminClient
	readonly onSaved: () => Promise<void> | void
}

export function OntologyEditor(props: OntologyEditorProps) {
	const draftApi = useOntologyDraft(props.initial)
	const { draft, isDirty } = draftApi

	const [view, setView] = useState<ViewKey>("diagram")
	const [selection, setSelection] = useState<MetaSelection>(null)
	const [packModalOpen, setPackModalOpen] = useState(false)
	const [masterModalOpen, setMasterModalOpen] = useState(false)
	const [importModalOpen, setImportModalOpen] = useState(false)
	const [addTarget, setAddTarget] = useState<AddTarget>(null)
	const [packs, setPacks] = useState<ReadonlyArray<OntologyPack>>([])
	const [presets, setPresets] = useState<ReadonlyArray<OntologyPreset>>([])
	const [blocks, setBlocks] = useState<ReadonlyArray<OntologyBlock>>([])
	const [masterShapes, setMasterShapes] = useState<ReadonlyArray<ShapeDef>>([])
	const [packsLoading, setPacksLoading] = useState(false)
	const [masterLoading, setMasterLoading] = useState(false)
	const [usage, setUsage] = useState<OntologyUsage | undefined>()
	const [saveError, setSaveError] = useState<string | undefined>()

	const [previewOpen, setPreviewOpen] = useState(false)
	const [previewLoading, setPreviewLoading] = useState(false)
	const [previewViolations, setPreviewViolations] = useState<OntologyViolations | undefined>()
	const [saving, setSaving] = useState(false)

	// ── 초기 데이터 로드 ───────────────────────────────
	useEffect(() => {
		let cancelled = false
		void (async () => {
			try {
				const u = await props.client.getOntologyUsage(props.slug)
				if (!cancelled) setUsage(u)
			} catch {
				// best-effort — usage 가 없어도 에디터는 동작한다
			}
		})()
		return () => {
			cancelled = true
		}
	}, [props.client, props.slug])

	// selection 이 가리키는 객체가 draft 변경으로 stale 해지면 동기화
	useEffect(() => {
		if (!selection) return
		if (selection.type === "kind") {
			const next = draft.kinds.find((k) => k.name === selection.value.name)
			if (!next) setSelection(null)
			else if (next !== selection.value) setSelection({ type: "kind", value: next })
		} else if (selection.type === "domain") {
			const next = draft.domains.find((d) => d.name === selection.value.name)
			if (!next) setSelection(null)
			else if (next !== selection.value) setSelection({ type: "domain", value: next })
		} else if (selection.type === "relation") {
			const next = draft.relations.find((r) => r.name === selection.value.name)
			if (!next) setSelection(null)
			else if (next !== selection.value) setSelection({ type: "relation", value: next })
		}
	}, [draft, selection])

	// ── Pack + Preset + Block 카탈로그 로드 (모달 열 때 한 번) ────
	// blocks 까지 함께 로드해 PackPicker 의 "블럭 드릴다운" + 카테고리 분포에 즉시 사용.
	const loadCatalogs = useCallback(async () => {
		if (packs.length > 0 && presets.length > 0 && blocks.length > 0) return
		setPacksLoading(true)
		try {
			const [packList, presetList, blockList] = await Promise.all([
				props.client.getOntologyPacks(),
				props.client.getOntologyPresets(),
				props.client.getOntologyBlocks()
			])
			setPacks(packList)
			setPresets(presetList)
			setBlocks(blockList)
		} catch {
			// 무시 — 모달 내부에서 empty 상태로 노출
		} finally {
			setPacksLoading(false)
		}
	}, [packs.length, presets.length, blocks.length, props.client])

	const onEditPacks = useCallback(() => {
		void loadCatalogs()
		setPackModalOpen(true)
	}, [loadCatalogs])

	// ── MasterComposer 데이터 로드 + 모달 열기 ────────────────────────
	const loadMasterData = useCallback(async () => {
		// blocks/presets 는 이미 로드됐을 수 있음. masterShapes 만 추가 로드.
		if (masterShapes.length > 0 && presets.length > 0 && blocks.length > 0) return
		setMasterLoading(true)
		try {
			const data = await loadMasterComposerData(props.client)
			setBlocks(data.blocks)
			if (data.masterPreset) {
				// presets 에 masterPreset 만 있으면 안 되므로 전체 presets 도 로드.
				// 위 loadMasterComposerData 가 getOntologyPresets 를 이미 호출했지만 여기서는
				// presets 상태 보존을 위해 별도 호출.
				if (presets.length === 0) {
					const presetList = await props.client.getOntologyPresets()
					setPresets(presetList)
				}
			}
			setMasterShapes(data.masterShapes)
		} catch {
			// 무시 — 모달 내부 empty 상태
		} finally {
			setMasterLoading(false)
		}
	}, [masterShapes.length, presets.length, blocks.length, props.client])

	const onEditMaster = useCallback(() => {
		void loadMasterData()
		setMasterModalOpen(true)
	}, [loadMasterData])

	const packById = useMemo(() => new Map(packs.map((p) => [p.id, p])), [packs])

	// ── 액션 핸들러 ────────────────────────────────────
	const onExport = useCallback(() => {
		const blob = new Blob([JSON.stringify(draft, null, 2)], { type: "application/json" })
		const url = URL.createObjectURL(blob)
		const a = document.createElement("a")
		a.href = url
		a.download = `ontology-${props.slug}.json`
		document.body.appendChild(a)
		a.click()
		document.body.removeChild(a)
		URL.revokeObjectURL(url)
	}, [draft, props.slug])

	const onDiscard = useCallback(() => {
		draftApi.discard()
		setSelection(null)
		setSaveError(undefined)
	}, [draftApi])

	const onSave = useCallback(async () => {
		setSaveError(undefined)
		setPreviewLoading(true)
		setPreviewOpen(true)
		try {
			const v = await props.client.previewOntologySlice(props.slug, draft)
			setPreviewViolations(v)
		} catch (err) {
			setPreviewOpen(false)
			setSaveError((err as Error).message)
		} finally {
			setPreviewLoading(false)
		}
	}, [draft, props.client, props.slug])

	const confirmSave = useCallback(async () => {
		setSaving(true)
		setSaveError(undefined)
		try {
			await props.client.updateOntologySlice(props.slug, draft)
			draftApi.commit()
			setPreviewOpen(false)
			setPreviewViolations(undefined)
			await props.onSaved()
		} catch (err) {
			setSaveError((err as Error).message)
		} finally {
			setSaving(false)
		}
	}, [draft, draftApi, props])

	const onAbsorbOne = useCallback(
		(type: "kinds" | "domains" | "relations", name: string) => {
			if (type === "kinds") {
				draftApi.addKind({ name, displayName: name })
			} else if (type === "domains") {
				draftApi.addDomain({ name, displayName: name })
			} else {
				draftApi.addRelation({ name, displayName: name })
			}
		},
		[draftApi]
	)

	const onAbsorbAll = useCallback(() => {
		if (usage) draftApi.absorbOrphans(usage)
	}, [draftApi, usage])

	const onAddConfirm = useCallback(
		(input: { readonly name: string; readonly displayName: string }) => {
			if (addTarget === "kind") {
				const kind: KindDef = { name: input.name, displayName: input.displayName }
				draftApi.addKind(kind)
				setSelection({ type: "kind", value: kind })
			} else if (addTarget === "domain") {
				const domain: DomainDef = { name: input.name, displayName: input.displayName }
				draftApi.addDomain(domain)
				setSelection({ type: "domain", value: domain })
			} else if (addTarget === "relation") {
				const rel: RelationDef = { name: input.name, displayName: input.displayName }
				draftApi.addRelation(rel)
				setSelection({ type: "relation", value: rel })
			}
			setAddTarget(null)
		},
		[addTarget, draftApi]
	)

	const existingNamesForAdd = useMemo((): ReadonlyArray<string> => {
		if (addTarget === "kind") return draft.kinds.map((k) => k.name)
		if (addTarget === "domain") return draft.domains.map((d) => d.name)
		if (addTarget === "relation") return draft.relations.map((r) => r.name)
		return []
	}, [addTarget, draft])

	return (
		<div className="ontology-editor">
			<OntologyHero
				draft={draft}
				isDirty={isDirty}
				usage={usage}
				saving={saving}
				onEditPacks={onEditPacks}
				onEditMaster={onEditMaster}
				onImport={() => setImportModalOpen(true)}
				onExport={onExport}
				onSave={() => void onSave()}
				onDiscard={onDiscard}
			/>

			{saveError && (
				<div className="alert alert-danger" style={{ marginBottom: 14 }}>
					{saveError}
				</div>
			)}

			<div className="ontology-view-tabs">
				<button
					type="button"
					className={`ontology-view-tab${view === "diagram" ? " is-active" : ""}`}
					onClick={() => setView("diagram")}
				>
					다이어그램
				</button>
				<button
					type="button"
					className={`ontology-view-tab${view === "catalog" ? " is-active" : ""}`}
					onClick={() => setView("catalog")}
				>
					카탈로그
				</button>
			</div>

			{view === "diagram" ? (
				<div className="ontology-diagram-layout">
					<div className="ontology-diagram-main">
						<OntologyDiagram
							slice={draft}
							onSelectKind={(kind) => setSelection(kind ? { type: "kind", value: kind } : null)}
						/>
						<div className="ontology-diagram-actions">
							<button type="button" className="btn btn-ghost btn-sm" onClick={() => setAddTarget("kind")}>
								+ 종류 추가
							</button>
							<button type="button" className="btn btn-ghost btn-sm" onClick={() => setAddTarget("domain")}>
								+ 도메인 추가
							</button>
							<button type="button" className="btn btn-ghost btn-sm" onClick={() => setAddTarget("relation")}>
								+ 관계 추가
							</button>
						</div>
					</div>
					<aside className="ontology-diagram-side">
						<MetaEditPanel
							selection={selection}
							shapes={draft.shapes}
							onUpdateKind={draftApi.updateKind}
							onUpdateDomain={draftApi.updateDomain}
							onUpdateRelation={draftApi.updateRelation}
							onRemoveShape={draftApi.removeShape}
							onClose={() => setSelection(null)}
						/>
					</aside>
				</div>
			) : (
				<div className="ontology-catalog-layout">
					<div className="ontology-catalog-main">
						<OntologyCatalog
							draft={draft}
							usage={usage}
							selection={selection}
							actions={draftApi}
							onSelect={setSelection}
							onAddKind={() => setAddTarget("kind")}
							onAddDomain={() => setAddTarget("domain")}
							onAddRelation={() => setAddTarget("relation")}
							onAbsorbOrphan={onAbsorbOne}
							onAbsorbAll={onAbsorbAll}
						/>
					</div>
					<aside className="ontology-diagram-side">
						<MetaEditPanel
							selection={selection}
							shapes={draft.shapes}
							onUpdateKind={draftApi.updateKind}
							onUpdateDomain={draftApi.updateDomain}
							onUpdateRelation={draftApi.updateRelation}
							onRemoveShape={draftApi.removeShape}
							onClose={() => setSelection(null)}
						/>
					</aside>
				</div>
			)}

			{packModalOpen && (
				<PackPicker
					packs={packs}
					presets={presets}
					blocks={blocks}
					client={props.client}
					loading={packsLoading}
					{...(draft.packIds ? { currentPackIds: draft.packIds } : {})}
					hasUnsavedChanges={isDirty}
					onClose={() => setPackModalOpen(false)}
					onApply={(selectedIds) => {
						const selectedPacks = selectedIds.flatMap((id) => {
							const p = packById.get(id)
							return p ? [p] : []
						})
						const merged = mergePacks(selectedPacks)
						draftApi.reset({ ...merged, packIds: selectedIds })
						setSelection(null)
						setPackModalOpen(false)
					}}
				/>
			)}

			{masterModalOpen && (
				<MasterComposer
					masterPreset={presets.find((p) => p.id === "pion-master")}
					blocks={blocks}
					masterShapes={masterShapes}
					loading={masterLoading}
					{...(draft.excludedBlockIds ? { initialExcludedBlockIds: draft.excludedBlockIds } : {})}
					{...(draft.excludedShapes ? { initialExcludedShapes: draft.excludedShapes } : {})}
					hasUnsavedChanges={isDirty}
					onClose={() => setMasterModalOpen(false)}
					onApply={(slice) => {
						draftApi.reset(slice)
						setSelection(null)
						setMasterModalOpen(false)
					}}
				/>
			)}

			{importModalOpen && (
				<ImportJsonModal
					onClose={() => setImportModalOpen(false)}
					onImport={(slice) => {
						draftApi.reset(slice)
						setSelection(null)
						setImportModalOpen(false)
					}}
				/>
			)}

			{addTarget && (
				<AddEntryModal
					type={addTarget}
					existingNames={existingNamesForAdd}
					onClose={() => setAddTarget(null)}
					onCreate={onAddConfirm}
				/>
			)}

			{previewOpen && (
				<PreviewImpactModal
					violations={
						previewViolations ?? {
							unknownKinds: [],
							unknownDomains: [],
							unknownRelations: [],
							shapeViolations: []
						}
					}
					loading={previewLoading}
					saving={saving}
					onCancel={() => {
						setPreviewOpen(false)
						setPreviewViolations(undefined)
					}}
					onConfirm={() => void confirmSave()}
				/>
			)}
		</div>
	)
}
